import clsx from 'clsx';
import { CollectionListElementDataType } from '@/components/sections/builder/elements/body/collection-list/type';
import { WrapperChild } from '@/components/sections/builder/wrapper';
import Link from 'next/link';
import { Button } from '@/components/ui/button';
import { ElementProps } from '@/components/sections/builder/types';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from '@/components/ui/carousel';
import { cn, isEmpty } from '@/lib/utils';
import { useParams, useSearchParams } from 'next/navigation';
import React from 'react';

export default function ElementContent({ element, preview, theme }: ElementProps) {
  const query = useSearchParams();
  const params = useParams();
  const themeId = query.get('theme_id') || '';
  const token = query.get('token') || '';
  const slug = params.slug || '';
  const data = element.data as CollectionListElementDataType;
  const childs = element.childs || [];

  return (
    <>
      <section
        className={clsx(
          'background-color',
          data.background?.type === 'color' && 'background-color',
          data.background?.type === 'image' && 'background-image'
        )}
      >
        <div className='theme_layout_page_width mx-auto duration-100'>
          <div className='collection-list-padding relative'>
            {data.overlay && <div className={clsx('overlay absolute inset-0')} />}
            <div className='relative px-6 py-6 lg:px-6 lg:py-8'>
              <div
                className={cn(
                  'flex flex-col',
                  data.desktopLayout === 'row' && 'gap-3 md:flex-col lg:flex-col',
                  data.desktopLayout === 'column' && '  gap-5  md:flex-row lg:flex-row',
                  theme?.collection?.textAlignment === 'left' && '[&>*]:text-left',
                  theme?.collection?.textAlignment === 'center' && '[&>*]:text-center',
                  theme?.collection?.textAlignment === 'right' && '[&>*]:text-right'
                )}
              >
                <div
                  className={cn(
                    'mb-4',
                    data.isShowViewAll && data.positionViewAll === 'top' && 'w-full'
                  )}
                >
                  <div
                    className={cn(
                      'flex items-center gap-2 md:gap-3 xl:gap-4',
                      data.contentAlign === 'left' && 'justify-between',
                      data.contentAlign === 'center' && 'justify-center',
                      data.contentAlign === 'right' && 'flex-row-reverse justify-between',
                      !data.textLabel && 'mb-8'
                    )}
                  >
                    <h2
                      className={cn(
                        data.colorSchemeCollectionList?.text && 'text-color-primary',
                        'theme_font_heading text-base tracking-tight',
                        'theme_font_heading text-base font-bold tracking-tight',
                        data.headingSize === 'large' &&
                          'text-xl sm:text-3xl md:text-4xl lg:text-5xl',
                        data.headingSize === 'medium' &&
                          'text-xl sm:text-2xl md:text-3xl lg:text-4xl',
                        data.headingSize === 'small' && 'text-lg sm:text-xl md:text-2xl lg:text-3xl'
                      )}
                      data-animation='true'
                      dangerouslySetInnerHTML={{ __html: data.headingLabel }}
                    />

                    {data.isShowViewAll && (
                      <Link
                        href={`/collection-list${
                          token
                            ? `?theme_id=${themeId}&token=${token}`
                            : preview
                            ? `?theme_id=${themeId}&isPreview=true`
                            : ''
                        }`}
                        className={cn(data.positionViewAll === 'bottom' && 'hidden')}
                      >
                        <Button
                          variant={data.viewAllStyle as 'default'}
                          className={cn(
                            'theme_font_heading text-sm font-bold tracking-tight sm:text-base',
                            data.viewAllStyle === 'link' && 'p-0'
                          )}
                          style={
                            !isEmpty(data.colorSchemeCollectionList) &&
                            data.viewAllStyle === 'default'
                              ? {
                                  backgroundColor:
                                    data.colorSchemeCollectionList?.btnSolidBackgroud,
                                  color: data?.colorSchemeCollectionList?.btnSolidlabel,
                                }
                              : data.viewAllStyle === 'link'
                              ? {
                                  color: data.colorSchemeCollectionList?.btnSolidlabel,
                                }
                              : {}
                          }
                        >
                          {data.labelViewAll}
                        </Button>
                      </Link>
                    )}
                  </div>
                  {data.textLabel && (
                    <div
                      className={cn(
                        'text-color-secondary mt-4 font-thin',
                        data.textStyle === 'body' && 'text-base md:text-lg lg:text-xl',
                        data.textStyle === 'subtitle' && 'text-sm md:text-base lg:text-lg',
                        data.textStyle === 'uppercase' &&
                          'text-base uppercase md:text-lg lg:text-xl'
                      )}
                      data-animation='true'
                      dangerouslySetInnerHTML={{ __html: data.textLabel }}
                    />
                  )}
                </div>
                <div
                  className={cn(
                    `theme_layout_space_grid_horizontal theme_layout_space_grid_vertical desktop-grid-col w-full place-content-center items-stretch duration-300 ease-in-out`,
                    data.enableSwipeMobile ? 'hidden md:grid' : 'grid',
                    data.totalColMobile === 'three_columns' && 'grid-cols-3',
                    data.totalColMobile === 'two_columns' && 'grid-cols-2',
                    data.totalColMobile === 'one_columns' && 'grid-cols-1'
                  )}
                >
                  {childs.map((child) => (
                    <WrapperChild
                      child={{
                        ...child,
                        parentData: data,
                        theme: theme,
                      }}
                      key={child.id}
                      parentType={element.type}
                      preview={preview}
                    />
                  ))}
                </div>

                <Carousel
                  className={cn('p-4', data.enableSwipeMobile ? 'grid md:hidden' : 'hidden')}
                  opts={{ loop: true, slidesToScroll: 1, align: 'start' }}
                >
                  <CarouselContent>
                    {childs.map((child) => (
                      <CarouselItem
                        className={cn(
                          data.totalColMobile === 'one_columns' && 'basis-full',
                          data.totalColMobile === 'two_columns' && 'basis-1/2',
                          data.totalColMobile === 'three_columns' && 'basis-1/3'
                        )}
                        key={child.id}
                      >
                        <WrapperChild
                          child={{
                            ...child,
                            parentData: data,
                            theme: theme,
                          }}
                          parentType={element.type}
                          preview={preview}
                        />
                      </CarouselItem>
                    ))}
                  </CarouselContent>
                  <CarouselPrevious className='left-4 h-12 w-12 [&>svg]:h-6 [&>svg]:w-6' />
                  <CarouselNext className='right-4 h-12 w-12 [&>svg]:h-6 [&>svg]:w-6' />
                </Carousel>
              </div>
              {data.isShowViewAll && data.positionViewAll === 'bottom' && (
                <div className={cn('mt-5 flex items-center justify-center ')}>
                  <Link
                    href={`/collection-list${
                      token
                        ? `?theme_id=${themeId}&token=${token}&slug=${slug}`
                        : preview
                        ? `?theme_id=${themeId}&isPreview=true&slug=${slug}`
                        : ''
                    }`}
                  >
                    <Button
                      variant={data.viewAllStyle as 'default'}
                      className={cn(
                        'theme_font_heading mx-auto flex w-fit items-center px-4 text-sm tracking-tight sm:text-base',
                        data.viewAllStyle === 'link' && 'button-link-scheme p-0'
                      )}
                      style={
                        !isEmpty(data.colorSchemeCollectionList) && data.viewAllStyle === 'default'
                          ? {
                              backgroundColor: data.colorSchemeCollectionList?.btnSolidBackgroud,
                              color: data?.colorSchemeCollectionList?.btnSolidlabel,
                            }
                          : {}
                      }
                    >
                      {data.labelViewAll}
                    </Button>
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
      <style jsx global>
        {`
          .flex_basis_${element.id} {
            flex-basis: ${data.totalColMobile === 'two_columns' ? '50%' : '100%'} !important;
          }
        `}
      </style>
      <style jsx>
        {`
          @media (min-width: 768px) {
            .desktop-grid-col {
              grid-template-columns: repeat(${data.totalCol || 1}, minmax(0, 1fr));
            }
          }
          @media (min-width: 768px) {
            .collection-list-padding {
              padding-top: ${data.padding?.top || 0}px;
              padding-bottom: ${data.padding?.bottom || 0}px;
              padding-left: ${data.padding?.left || 0}px;
              padding-right: ${data.padding?.right || 0}px;
            }
          }
          .overlay {
            background-color: ${data.overlay};
          }
          .text-color-primary {
            color: ${data.colorSchemeCollectionList?.text};
          }
          .text-color-secondary {
            color: ${data.colorSchemeCollectionList?.textSecondary};
          }
          .button-link-scheme {
            color: ${data.colorSchemeCollectionList?.btnSolidlabel};
          }
          .background-color {
            background-color: ${data.background?.backgroundColor
              ? data.background?.backgroundColor
              : data.colorSchemeCollectionList?.background};
            --tw-bg-opacity: ${data.background?.backgroundOpacity};
          }
          .background-image {
            background-image: url(${data.background?.backgroundImage});
            background-repeat: no-repeat;
            background-size: ${data.background?.backgroundSize};
            background-position: ${data.background?.backgroundPosition};
            background-attachment: ${data.background?.backgroundAttachment};
          }
        `}
      </style>
    </>
  );
}
